import { apiClient } from './api'

// ========================================
// Types & Interfaces
// ========================================

export type Association = {
  id: string
  customer_id: string
  reseller_id: string
  status: 'pending' | 'active' | 'rejected' | 'suspended' | 'cancelled'

  // Timestamps
  requested_at: string
  approved_at: string | null
  rejected_at: string | null
  suspended_at: string | null
  cancelled_at: string | null
  created_at: string
  updated_at: string

  // Motivos/Razões
  rejection_reason: string | null
  suspension_reason: string | null
  cancellation_reason: string | null

  // Aprovadores/Suspensores/Canceladores
  approved_by: string | null
  suspended_by: string | null
  cancelled_by: string | null

  // Relacionamentos (quando available)
  customer?: OrganizationInfo
  reseller?: OrganizationInfo
  approved_by_user?: UserInfo
  suspended_by_user?: UserInfo
  cancelled_by_user?: UserInfo
}

export type OrganizationInfo = {
  id: string
  name: string
  type: string
  is_active: boolean
}

export type UserInfo = {
  id: string
  email: string
  full_name: string | null
}

export type AssociationListResponse = {
  associations: Association[]
  total: number
  skip: number
  limit: number
}

export type AssociationStatsResponse = {
  pending_count: number
  active_count: number
  rejected_count: number
  suspended_count: number
  cancelled_count: number
  total_count: number
}

export type ResellerMarketplaceInfo = {
  id: string
  name: string
  description: string | null
  is_active: boolean
  has_association: boolean
  association_status: string | null
  association_id: string | null
}

export type AvailableResellersResponse = {
  resellers: ResellerMarketplaceInfo[]
  total: number
}

// ========================================
// Request Schemas
// ========================================

export type AssociationRequestCreate = {
  reseller_id: string
}

export type AssociationApproveRequest = {
  initial_credit?: number
  credit_limit?: number
  welcome_message?: string
}

export type AssociationRejectRequest = {
  reason: string
}

export type AssociationSuspendRequest = {
  reason: string
}

export type AssociationCancelRequest = {
  reason: string
}

// ========================================
// API Client - Reseller Endpoints
// ========================================

export const associationsApi = {
  // ========================================
  // Reseller: Association Management
  // ========================================

  /**
   * Lista solicitações de associação pendentes
   */
  listPendingAssociations: (params?: {
    skip?: number
    limit?: number
  }): Promise<AssociationListResponse> => {
    const queryParams = new URLSearchParams()
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString())
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())

    const queryString = queryParams.toString()
    const url = queryString
      ? `/api/v1/reseller/associations/pending?${queryString}`
      : `/api/v1/reseller/associations/pending`

    return apiClient.get(url)
  },

  /**
   * Aprova solicitação de associação
   */
  approveAssociation: (
    associationId: string,
    data: AssociationApproveRequest,
  ): Promise<Association> => {
    return apiClient.post(`/api/v1/reseller/associations/${associationId}/approve`, data)
  },

  /**
   * Rejeita solicitação de associação
   */
  rejectAssociation: (
    associationId: string,
    data: AssociationRejectRequest,
  ): Promise<Association> => {
    return apiClient.post(`/api/v1/reseller/associations/${associationId}/reject`, data)
  },

  /**
   * Suspende associação ativa
   */
  suspendAssociation: (
    associationId: string,
    data: AssociationSuspendRequest,
  ): Promise<Association> => {
    return apiClient.post(`/api/v1/reseller/associations/${associationId}/suspend`, data)
  },

  /**
   * Reativa associação suspensa
   */
  reactivateAssociation: (associationId: string): Promise<Association> => {
    return apiClient.post(`/api/v1/reseller/associations/${associationId}/reactivate`, {})
  },

  /**
   * Lista todas as associações do reseller
   */
  listAssociations: (params?: {
    status?: string
    skip?: number
    limit?: number
  }): Promise<AssociationListResponse> => {
    const queryParams = new URLSearchParams()
    if (params?.status) queryParams.append('status_filter', params.status)
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString())
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())

    const queryString = queryParams.toString()
    const url = queryString
      ? `/api/v1/reseller/associations?${queryString}`
      : `/api/v1/reseller/associations`

    return apiClient.get(url)
  },

  /**
   * Obtém estatísticas de associações
   */
  getAssociationStats: (): Promise<AssociationStatsResponse> => {
    return apiClient.get('/api/v1/reseller/associations/stats')
  },

  // ========================================
  // Helper Functions
  // ========================================

  /**
   * Retorna cor do badge de status
   */
  getStatusColor: (status: string): string => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      active: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
      suspended: 'bg-orange-100 text-orange-800 border-orange-200',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-200',
    }
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200'
  },

  /**
   * Retorna label do status em português
   */
  getStatusLabel: (status: string): string => {
    const labels: Record<string, string> = {
      pending: 'Pendente',
      active: 'Ativa',
      rejected: 'Rejeitada',
      suspended: 'Suspensa',
      cancelled: 'Cancelada',
    }
    return labels[status] || status
  },

  /**
   * Retorna ícone do status
   */
  getStatusIcon: (status: string): string => {
    const icons: Record<string, string> = {
      pending: '⏱️',
      active: '✅',
      rejected: '❌',
      suspended: '⏸️',
      cancelled: '🚫',
    }
    return icons[status] || '❓'
  },

  /**
   * Formata data de timestamp
   */
  formatDate: (timestamp: string | null): string => {
    if (!timestamp) return '-'
    const date = new Date(timestamp)
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  },

  /**
   * Verifica se associação pode ser aprovada
   */
  canApprove: (association: Association): boolean => {
    return association.status === 'pending'
  },

  /**
   * Verifica se associação pode ser rejeitada
   */
  canReject: (association: Association): boolean => {
    return association.status === 'pending'
  },

  /**
   * Verifica se associação pode ser suspensa
   */
  canSuspend: (association: Association): boolean => {
    return association.status === 'active'
  },

  /**
   * Verifica se associação pode ser reativada
   */
  canReactivate: (association: Association): boolean => {
    return association.status === 'suspended'
  },

  /**
   * Verifica se associação pode ser cancelada (cliente)
   */
  canCancel: (association: Association): boolean => {
    return association.status === 'active' || association.status === 'pending'
  },
}

// ========================================
// API Client - End Customer Endpoints
// ========================================

export const associationsApiCustomer = {
  /**
   * Lista resellers disponíveis para associação
   */
  getAvailableResellers: (params?: {
    skip?: number
    limit?: number
  }): Promise<AvailableResellersResponse> => {
    const queryParams = new URLSearchParams()
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString())
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())

    const queryString = queryParams.toString()
    const url = queryString
      ? `/api/v1/end-customer/resellers/available?${queryString}`
      : `/api/v1/end-customer/resellers/available`

    return apiClient.get(url)
  },

  /**
   * Solicita associação com um reseller
   */
  requestAssociation: (resellerId: string): Promise<Association> => {
    return apiClient.post('/api/v1/end-customer/associations/request', {
      reseller_id: resellerId,
    })
  },

  /**
   * Lista todas as associações do cliente
   */
  listMyAssociations: (params?: {
    status?: string
    skip?: number
    limit?: number
  }): Promise<AssociationListResponse> => {
    const queryParams = new URLSearchParams()
    if (params?.status) queryParams.append('status_filter', params.status)
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString())
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())

    const queryString = queryParams.toString()
    const url = queryString
      ? `/api/v1/end-customer/associations?${queryString}`
      : `/api/v1/end-customer/associations`

    return apiClient.get(url)
  },

  /**
   * Cancela uma associação
   */
  cancelAssociation: (associationId: string, reason: string): Promise<Association> => {
    return apiClient.post(`/api/v1/end-customer/associations/${associationId}/cancel`, {
      reason,
    })
  },

  // Reutilizar helpers do associationsApi
  getStatusColor: associationsApi.getStatusColor,
  getStatusLabel: associationsApi.getStatusLabel,
  getStatusIcon: associationsApi.getStatusIcon,
  formatDate: associationsApi.formatDate,
  canCancel: associationsApi.canCancel,
}

export default associationsApi
